import React, { useEffect, useState, useRef } from 'react';
import {
  exchangeCodeForToken,
  fetchAllLikedSongs,
  sortSongsIntoGenres,
  createGenrePlaylists,
} from './spotify';
import styles from './SpotifyCallback.module.css';

// ─── States: scanning → selecting → creating → done | error ──────────

const SpotifyCallback = () => {
  const [status, setStatus]           = useState('scanning');
  const [step, setStep]               = useState('connecting to spotify...');
  const [genreBuckets, setGenreBuckets] = useState({});
  const [selected, setSelected]       = useState(new Set());
  const [token, setToken]             = useState(null);
  const [results, setResults]         = useState([]);
  const [error, setError]             = useState(null);
  const hasRun = useRef(false);

  // ─── 1. Scan phase ────────────────────────────────────────────────
  useEffect(() => {
    if (hasRun.current) return;
    hasRun.current = true;

    const params     = new URLSearchParams(window.location.search);
    const code       = params.get('code');
    const errorParam = params.get('error');

    if (errorParam) { setStatus('error'); setError(`Spotify said: ${errorParam}`); return; }
    if (!code)       { setStatus('error'); setError('No auth code in URL. Try connecting again.'); return; }

    (async () => {
      try {
        setStep('connecting to spotify...');
        const accessToken = await exchangeCodeForToken(code);
        setToken(accessToken);

        const songs = await fetchAllLikedSongs(accessToken, setStep);
        setStep(`analysing ${songs.length} songs...`);

        const buckets = await sortSongsIntoGenres(songs, accessToken, setStep);

        // Sort genres by song count desc, filter empty
        const sorted = Object.fromEntries(
          Object.entries(buckets)
            .filter(([, uris]) => uris.length > 0)
            .sort(([, a], [, b]) => b.length - a.length)
        );

        setGenreBuckets(sorted);
        setSelected(new Set(Object.keys(sorted))); // pre-select all
        setStep(`found ${Object.keys(sorted).length} genres across ${songs.length} songs`);
        // Brief pause so the user sees the count before the screen transitions
        await new Promise(r => setTimeout(r, 1200));
        setStatus('selecting');
      } catch (err) {
        console.error(err);
        setStatus('error');
        setError(err.message || 'Something went wrong during scan');
      }
    })();
  }, []);

  // ─── 2. Selection handlers ─────────────────────────────────────────
  const toggle = (genre) => setSelected(prev => {
    const next = new Set(prev);
    next.has(genre) ? next.delete(genre) : next.add(genre);
    return next;
  });

  const selectAll  = () => setSelected(new Set(Object.keys(genreBuckets)));
  const selectNone = () => setSelected(new Set());

  // ─── 3. Create phase ───────────────────────────────────────────────
  const handleCreate = async () => {
    setStatus('creating');
    const chosenBuckets = Object.fromEntries(
      Object.entries(genreBuckets).filter(([g]) => selected.has(g))
    );
    try {
      const playlists = await createGenrePlaylists(chosenBuckets, token, setStep);
      setResults(playlists);
      setStatus('done');
    } catch (err) {
      console.error(err);
      setStatus('error');
      setError(err.message || 'Failed to create playlists');
    }
  };

  const totalSelected = [...selected].reduce((sum, g) => sum + (genreBuckets[g]?.length || 0), 0);

  // ─── Render ────────────────────────────────────────────────────────
  return (
    <div className={styles.page}>
      <div className={styles.container}>

        {/* ── Scanning ── */}
        {status === 'scanning' && (
          <div className={styles.state}>
            <div className={styles.spinner}><div className={styles.spinnerInner} /></div>
            <div className={styles.heading}>scanning your taste</div>
            <p className={styles.step}>{step}</p>
            <p className={styles.note}>don't close this tab</p>
          </div>
        )}

        {/* ── Genre picker ── */}
        {status === 'selecting' && (
          <div className={`${styles.state} ${styles.selectState}`}>
            <div className={styles.heading}>pick your playlists</div>
            <p className={styles.subtitle}>
              found {Object.keys(genreBuckets).length} genres —
              select which ones to create
            </p>

            <div className={styles.quickActions}>
              <button className={styles.qbtn} onClick={selectAll}>all</button>
              <button className={styles.qbtn} onClick={selectNone}>none</button>
            </div>

            <div className={styles.genreGrid}>
              {Object.entries(genreBuckets).map(([genre, uris]) => {
                const active = selected.has(genre);
                return (
                  <button
                    key={genre}
                    className={`${styles.genrePill} ${active ? styles.pillActive : ''}`}
                    onClick={() => toggle(genre)}
                  >
                    <span className={styles.pillName}>{genre}</span>
                    <span className={styles.pillCount}>{uris.length}</span>
                  </button>
                );
              })}
            </div>

            <button
              className={styles.createBtn}
              onClick={handleCreate}
              disabled={selected.size === 0}
            >
              create {selected.size} playlist{selected.size !== 1 ? 's' : ''}
              <span className={styles.createSub}>{totalSelected} songs</span>
            </button>

            <a href="/idonthaveagf" className={styles.backLink}>cancel</a>
          </div>
        )}

        {/* ── Creating ── */}
        {status === 'creating' && (
          <div className={styles.state}>
            <div className={styles.spinner}><div className={styles.spinnerInner} /></div>
            <div className={styles.heading}>creating playlists</div>
            <p className={styles.step}>{step}</p>
            <p className={styles.note}>don't close this tab</p>
          </div>
        )}

        {/* ── Done ── */}
        {status === 'done' && (
          <div className={styles.state}>
            <div className={styles.successIcon}>✓</div>
            <div className={styles.heading}>done.</div>
            <p className={styles.subtitle}>created {results.length} playlists in your Spotify</p>
            <div className={styles.resultsList}>
              {results.map(r => (
                <a
                  key={r.id}
                  href={`https://open.spotify.com/playlist/${r.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.resultItem}
                >
                  <span className={styles.resultGenre}>{r.genre}</span>
                  <span className={styles.resultCount}>{r.count} songs →</span>
                </a>
              ))}
            </div>
            <a
              href="https://open.spotify.com"
              target="_blank"
              rel="noopener noreferrer"
              className={styles.btn}
            >
              open spotify →
            </a>
            <a href="/idonthaveagf" className={styles.backLink}>do it again</a>
          </div>
        )}

        {/* ── Error ── */}
        {status === 'error' && (
          <div className={styles.state}>
            <div className={styles.errorIcon}>!</div>
            <div className={styles.heading}>something broke</div>
            <p className={styles.errorMsg}>{error}</p>
            <a href="/idonthaveagf" className={styles.btn}>try again</a>
          </div>
        )}

      </div>
    </div>
  );
};

export default SpotifyCallback;
